import { forwardRef, HTMLProps, useRef } from 'react'
import clsx from 'classnames'
import { useTranslations } from 'next-intl'
import { useIntersection } from '@/shared/utils'
import { MediaAboutCard } from '@/entities/media-about-card'
import { Text } from '@/shared/ui'
import { IMassMediaPost } from '@/interfaces/media'
import { MediaAboutLoader } from './media-about-loader'
import style from './media-about-list.module.scss'

type MediaAboutAsyncListProps = HTMLProps<HTMLUListElement> & {
  cards: IMassMediaPost[]
  publications?: boolean
  anchorId?: string
  onCurrentPage?: (page: number) => void
  onFetchPage?: (page: number) => void
}

export const MediaAboutAsyncList = forwardRef<HTMLUListElement, MediaAboutAsyncListProps>((props, ref) => {
  const { cards, anchorId, publications, onCurrentPage, onFetchPage, ...restProps } = props
  const listRef = useRef<HTMLUListElement>(null)
  const translate = useTranslations()

  useIntersection({
    ref: listRef,
    onCurrentPage: page => {
      onCurrentPage?.(page)
    },
    options: {
      rootMargin: '-15% 0px -80% 0px',
    },
  })
  useIntersection({
    ref: listRef,
    onCurrentPage: (page, observerInstance) => {
      if (cards?.length) {
        observerInstance.disconnect()
      } else {
        onFetchPage?.(page)
      }
    },
    options: {
      rootMargin: '160% 0px 70% 0px',
    },
  })

  if (cards?.length === 0)
    return (
      <div className={style.empty}>
        <Text type="h1" color="dark">
          {translate('itemsNotFound')}
        </Text>
      </div>
    )

  return (
    <ul
      id={anchorId}
      style={cards ? {} : { minHeight: '110vh' }}
      ref={el => {
        // @ts-ignore
        ref?.(el)
        // @ts-ignore
        listRef.current = el
      }}
      className={style.list}
      {...restProps}
    >
      {cards ? (
        cards.map(({ date_published, title, image_preview, url, excerpt, source }, index) => (
          <MediaAboutCard
            className={clsx(style.item, { [style.publications]: publications })}
            key={index}
            title={title}
            date={date_published}
            image={image_preview}
            index={index}
            url={url}
            excerpt={excerpt}
            source={source}
          />
        ))
      ) : (
        <MediaAboutLoader className={clsx(style.item, style.publications, { [style.publications]: publications })} />
      )}
    </ul>
  )
})

MediaAboutAsyncList.displayName = 'MediaAboutAsyncList'
